import React from 'react';
import { motion } from 'motion/react';
import { BookOpen } from 'lucide-react';
import { BDAY_EASE } from '../theme';
import type { BirthdayContent } from '../content';
import { SectionBgImage, hasSectionBg } from './SectionBgImage';

interface BirthdayStorySectionProps {
  content: BirthdayContent;
}

export function BirthdayStorySection({ content }: BirthdayStorySectionProps) {
  const story = content.birthdayStory;
  if (!story || !story.content) return null;

  const bg = content.sectionBackgrounds.story;
  const active = hasSectionBg(bg);

  return (
    <section
      className="relative py-24 px-6 overflow-hidden"
      style={active ? undefined : { background: 'linear-gradient(180deg, #0f172a 0%, #172554 100%)' }}
    >
      <SectionBgImage bg={bg} />
      {/* glow blob */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full bg-blue-500/10 blur-3xl pointer-events-none" />

      <div className="relative max-w-2xl mx-auto">
        {/* heading */}
        <div className="text-center mb-10">
          <motion.div
            initial={{ scale: 0 }}
            whileInView={{ scale: 1 }}
            viewport={{ once: true }}
            transition={{ type: 'spring', stiffness: 250, damping: 18 }}
            className="inline-flex w-14 h-14 rounded-full bg-blue-500/20 border border-blue-400/30 items-center justify-center mb-5 mx-auto"
          >
            <BookOpen size={24} className="text-blue-300" />
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: -10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, ease: BDAY_EASE }}
            className="text-blue-400 text-xs font-bold uppercase tracking-[0.25em] mb-2"
          >
            📖 Our Story
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1, ease: BDAY_EASE }}
            className="text-3xl sm:text-4xl font-bold text-white"
          >
            {story.title || 'The Birthday Story'}
          </motion.h2>
        </div>

        {/* story card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.65, ease: BDAY_EASE }}
          className="bg-white/5 backdrop-blur-sm border border-blue-400/15 rounded-3xl p-8 sm:p-10"
        >
          <p className="text-blue-100 text-base sm:text-lg leading-relaxed whitespace-pre-line">
            {story.content}
          </p>

          {story.quote && (
            <motion.blockquote
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.25 }}
              className="mt-8 border-l-4 border-blue-400/50 pl-5"
            >
              <p className="text-blue-200 italic text-base leading-relaxed">
                "{story.quote}"
              </p>
              {story.author && (
                <p className="text-blue-400 text-sm font-semibold mt-2">
                  — {story.author}
                </p>
              )}
            </motion.blockquote>
          )}
        </motion.div>
      </div>
    </section>
  );
}
